import { useEffect, useRef } from "react";
import "./BugBuzzAnimation.css";

export default function BugBuzzAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];

    // Initial attacker animation - buzzing wings preparation
    attackerEl.style.transition = 'transform 0.3s ease-in-out, filter 0.3s ease-in-out';
    attackerEl.style.transform = 'scale(1.05)';
    attackerEl.style.filter = 'brightness(1.2) drop-shadow(0 0 10px #8BC34A)';
    
    // Create wing buzz aura around attacker
    const wingBuzzAura = document.createElement('div');
    wingBuzzAura.className = 'bug-buzz-wing-aura';
    wingBuzzAura.style.left = `${attackerCenterX}px`;
    wingBuzzAura.style.top = `${attackerCenterY}px`;
    container.appendChild(wingBuzzAura);
    elements.push(wingBuzzAura);
    
    // Create buzzing particles around attacker
    for (let i = 0; i < 12; i++) {
      const buzzTimer = setTimeout(() => {
        const buzzParticle = document.createElement('div');
        buzzParticle.className = 'bug-buzz-particle';
        
        const angle = (i / 12) * Math.PI * 2;
        const distance = 25 + Math.random() * 15;
        buzzParticle.style.left = `${attackerCenterX + Math.cos(angle) * distance}px`;
        buzzParticle.style.top = `${attackerCenterY + Math.sin(angle) * distance}px`;
        buzzParticle.style.setProperty('--buzz-delay', `${i * 0.03}s`);
        buzzParticle.style.setProperty('--buzz-rotation', `${Math.random() * 360}deg`);
        
        container.appendChild(buzzParticle);
        elements.push(buzzParticle);
        
        // Auto-remove particle
        const particleRemoveTimer = setTimeout(() => {
          if (buzzParticle.isConnected) {
            buzzParticle.remove();
          }
        }, 1000);
        timers.push(particleRemoveTimer);
      }, i * 40);
      timers.push(buzzTimer);
    }
    
    // Main Bug Buzz sound wave attack
    const soundWaveTimer = setTimeout(() => {
      // Create multiple sound waves traveling from attacker to defender
      for (let i = 0; i < 5; i++) {
        const waveTimer = setTimeout(() => {
          const soundWave = document.createElement('div');
          soundWave.className = 'bug-buzz-sound-wave';
          
          // Calculate direction and distance
          const dx = defenderCenterX - attackerCenterX;
          const dy = defenderCenterY - attackerCenterY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          
          soundWave.style.left = `${attackerCenterX}px`;
          soundWave.style.top = `${attackerCenterY}px`;
          soundWave.style.setProperty('--wave-distance', `${distance}px`);
          soundWave.style.setProperty('--wave-angle', `${angle}rad`);
          soundWave.style.setProperty('--wave-delay', `${i * 0.1}s`);
          soundWave.style.setProperty('--wave-size', `${60 + i * 20}px`);
          
          container.appendChild(soundWave);
          elements.push(soundWave);
          
          // Auto-remove wave
          const waveRemoveTimer = setTimeout(() => {
            if (soundWave.isConnected) {
              soundWave.remove();
            }
          }, 800);
          timers.push(waveRemoveTimer);
        }, i * 100);
        timers.push(waveTimer);
      }
      
      // Create buzzing distortion effect across the battlefield
      const distortionField = document.createElement('div');
      distortionField.className = 'bug-buzz-distortion-field';
      distortionField.style.left = `${attackerCenterX}px`;
      distortionField.style.top = `${attackerCenterY}px`;
      container.appendChild(distortionField);
      elements.push(distortionField);
      
    }, 400); // After buzz buildup
    
    timers.push(soundWaveTimer);

    // Impact effects and special defense reduction chance
    const impactTimer = setTimeout(() => {
      // Sonic impact explosion on defender
      const sonicImpact = document.createElement('div');
      sonicImpact.className = 'bug-buzz-sonic-impact';
      sonicImpact.style.left = `${defenderCenterX}px`;
      sonicImpact.style.top = `${defenderCenterY}px`;
      container.appendChild(sonicImpact);
      elements.push(sonicImpact);
      
      // Special Defense reduction chance (10%)
      const spDefReductionChance = Math.random();
      if (spDefReductionChance < 0.1) {
        const spDefReduction = document.createElement('div');
        spDefReduction.className = 'bug-buzz-spdef-reduction';
        spDefReduction.style.left = `${defenderCenterX}px`;
        spDefReduction.style.top = `${defenderCenterY - 40}px`;
        container.appendChild(spDefReduction);
        elements.push(spDefReduction);
        
        // Enhanced impact for stat reduction
        sonicImpact.classList.add('spdef-reduction-impact');
      }
      
      // Make defender react with vibration/shake
      defenderEl.classList.add('bug-buzz-hit');
      defenderEl.style.filter = 'brightness(1.3) hue-rotate(60deg) saturate(1.2)';
      
      // Create vibration lines around defender
      for (let i = 0; i < 8; i++) {
        const vibrationLine = document.createElement('div');
        vibrationLine.className = 'bug-buzz-vibration-line';
        
        const angle = (i / 8) * Math.PI * 2;
        const distance = 40 + Math.random() * 20;
        vibrationLine.style.left = `${defenderCenterX + Math.cos(angle) * distance}px`;
        vibrationLine.style.top = `${defenderCenterY + Math.sin(angle) * distance}px`;
        vibrationLine.style.setProperty('--vibration-angle', `${angle + Math.PI/2}rad`);
        vibrationLine.style.setProperty('--vibration-delay', `${i * 0.05}s`);
        
        container.appendChild(vibrationLine);
        elements.push(vibrationLine);
        
        const vibrationTimer = setTimeout(() => {
          if (vibrationLine.parentNode) {
            vibrationLine.remove();
          }
        }, 600 + Math.random() * 200);
        timers.push(vibrationTimer);
      }
      
      // Create sonic aftershock rings
      for (let i = 0; i < 3; i++) {
        const aftershockTimer = setTimeout(() => {
          const aftershock = document.createElement('div');
          aftershock.className = 'bug-buzz-aftershock';
          aftershock.style.left = `${defenderCenterX}px`;
          aftershock.style.top = `${defenderCenterY}px`;
          aftershock.style.setProperty('--aftershock-delay', `${i * 0.2}s`);
          aftershock.style.setProperty('--aftershock-size', `${80 + i * 30}px`);
          
          container.appendChild(aftershock);
          elements.push(aftershock);
          
          const aftershockRemoveTimer = setTimeout(() => {
            if (aftershock.parentNode) {
              aftershock.remove();
            }
          }, 800);
          timers.push(aftershockRemoveTimer);
        }, i * 150);
        timers.push(aftershockTimer);
      }
      
      // Reset defender after hit effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('bug-buzz-hit');
        defenderEl.style.filter = '';
      }, 700);
      timers.push(defenderResetTimer);
      
    }, 900); // Impact timing
    
    timers.push(impactTimer);

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1000);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1800);
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('bug-buzz-hit');
        defenderEl.style.filter = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.bug-buzz-wing-aura, .bug-buzz-particle, .bug-buzz-sound-wave, ' +
        '.bug-buzz-distortion-field, .bug-buzz-sonic-impact, .bug-buzz-spdef-reduction, ' +
        '.bug-buzz-vibration-line, .bug-buzz-aftershock'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

BugBuzzAnimation.moveType = "bug";
